import BaseStore from '@src/utils/store/base-store';
import { action, observable, reaction } from 'mobx';
import useMount from '@src/utils/hooks/use-mount';
import { message } from 'antd';
import $$tracker from '@src/tracker';
import BaseSwiperStore from '@src/components/swiper/base-swiper/store';
import LoginStore from '@src/utils/store/login-store';
import useModal from '@src/utils/hooks/use-modal';
import VerificationModal from './components/verification-modal';
import VerificationModalStore from './components/verification-modal/store';
import PhoneBindingModal from './components/phone-binding-modal';
import PhoneBindingModalStore from './components/phone-binding-modal/store';
import ReservationSuccessModal from './components/reservation-success-modal';
import ReservationSuccessModalStore from './components/reservation-success-modal/store';
import PaymentModal from './components/payment-modal';
import PaymentModalStore from './components/payment-modal/store';
import copy from 'copy-to-clipboard';
import qs from '@src/utils/query-string-util';
import Services from '@src/services/general';

const SIDE_NAVIGATION_TARGETS = ['reservation', 'gift-packs', 'month-card', 'activity-rules'];
const GIFT_SLOT_COUNT = 9;
const RESERVATION_HERO_VISIBLE_COUNT = 6;
const TEAM_ACTION_LABELS = {
  create_team: '创建购买队伍',
  join_team: '加入购买队伍',
  invite_friend: '复制组件链接',
  team_success: '组队购买成功',
  team_full: '队伍已满',
  team_unavailable: '暂不可用'
};

const createEmptyGiftSlot = giftId => ({
  giftId,
  name: '',
  purchaseCount: 0,
  price: 0,
  originalPrice: 0,
  pricePhase: 'closed',
  actionState: 'unavailable',
  actionLabel: '暂不可用',
  canPurchase: false,
  isActionDisabled: false,
  isPlaceholder: true
});

class MainStore extends BaseStore {
  loginStore = new LoginStore();
  getIsLogin = () => this.loginStore.isLogin;
  verificationModalStore = new VerificationModalStore({ onSuccess: result => this.handleReservationSuccess(result) });
  phoneBindingModalStore = new PhoneBindingModalStore();
  reservationSuccessModalStore = new ReservationSuccessModalStore({ closeCallback: () => this.handleReservationSuccessClose() });
  paymentModalStore = new PaymentModalStore({ closeCallback: () => this.handlePaymentClose() });
  @observable teamId = '';
  @observable isTeamGuestMode = false;
  @observable isGuestActivityEnded = false;
  @observable isHomeActivityEnded = false;
  @observable returnToGuestAfterReservation = false;
  @observable isOrdering = false;
  @observable accountTeamViewModel = null;
  @observable
  teamViewModel = {
    actionState: 'team_unavailable',
    actionLabel: TEAM_ACTION_LABELS.team_unavailable,
    teamId: '',
    memberCount: 0,
    canInteract: false
  };

  showBusinessMessage = text => message.info(text, 1);

  signIn() {
    return this.loginStore.signIn();
  }

  async handleReserveClick() {
    $$tracker.clickSubscribe();
    if (this.configViewModel.isReserved) {
      return false;
    }
    if (!this.loginStore.isLogin) {
      this.signIn();
      return false;
    }
    try {
      const response = await this.$ztService.getPhone();
      this.openVerificationModal(response && response.phone);
      return true;
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      if (Number(response.code) === -20101) {
        this.phoneBindingModal.open();
      } else {
        message.error(response.message || response.msg || '获取账号信息失败');
      }
      return false;
    }
  }

  openVerificationModal(phone) {
    this.verificationModalStore.setPhone(phone);
    this.verificationModal.open();
  }

  async handleReservationSuccess(result) {
    this.setConfigViewModel({ ...this.configViewModel, reservationState: 'reserved', isReserved: true });
    this.reservationSuccessModalStore.setResult({
      account: this.configViewModel.account,
      serverName: this.serverName,
      friends: result && result.friends
    });
    this.reservationSuccessModal.open();
    await Promise.all([this.loadConfig(), this.loadReservationRanking()]);
  }
  @observable serverName = '新服名称';
  @observable reservationCount = 0;
  @observable reservationHeroes = [];
  @observable featureTopicUrl = '';
  @observable isConfigReady = false;
  @observable
  configViewModel = {
    account: '',
    buyStatus: null,
    bindGold: 0,
    bindGoldText: '0',
    serverName: '新服名称',
    reservationState: 'unreserved',
    isReserved: false,
    purchasePhase: 'closed',
    isPurchaseOpen: false,
    teamState: 'team_unavailable',
    teamId: ''
  };
  @observable giftSlots = Array.from({ length: GIFT_SLOT_COUNT }, (item, index) => createEmptyGiftSlot(index + 1));
  moreBenefitsSwiper = new BaseSwiperStore({
    swiperOptions: {
      autoplay: {
        delay: 4000,
        disableOnInteraction: false
      },
      loop: true,
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true
      }
    }
  });
  @observable isSideNavigationVisible = true;
  @observable isSideNavigationCollapsed = false;
  @observable activeSideNavigationTarget = 'reservation';
  sideNavigationScrollTarget = '';
  sideNavigationScrollTimer = null;

  use() {
    super.use();
    this.verificationModal = useModal(VerificationModal, { store: this.verificationModalStore });
    this.phoneBindingModal = useModal(PhoneBindingModal, { store: this.phoneBindingModalStore });
    this.reservationSuccessModal = useModal(ReservationSuccessModal, { store: this.reservationSuccessModalStore });
    this.paymentModal = useModal(PaymentModal, { store: this.paymentModalStore });
    useMount(() => {
      // The header SDK truncates its label before invoking the login callback.
      const disposeAccountLabel = reaction(
        () => this.loginStore.userName,
        account => {
          const label = document.querySelector('.t_xpass_signin_u_name_i span');
          if (label && account) {
            label.textContent = account;
          }
        }
      );
      const disposeLoginState = this.loginStore.initialize(undefined, () => this.requestHeaderAccount());
      this.openReservationPreview();
      this.initializeBusinessState();
      this.handleScroll();
      window.addEventListener('scroll', this.handleScroll);
      return () => {
        disposeLoginState();
        disposeAccountLabel();
        window.removeEventListener('scroll', this.handleScroll);
        clearTimeout(this.sideNavigationScrollTimer);
      };
    });
  }

  openReservationPreview() {
    const hashQueryIndex = window.location.hash.indexOf('?');
    const params = {
      ...(hashQueryIndex >= 0 ? qs.parse(window.location.hash.slice(hashQueryIndex + 1)) : {}),
      ...qs.getSearchParams(),
      ...this.$searchParams
    };
    if (params.test_pre_modal !== '1') {
      return false;
    }
    this.reservationSuccessModalStore.setResult({
      account: '123****456',
      serverName: '新服名称',
      friends: params.pre_text === '2' ? ['XXX', 'XXX', 'XX'] : []
    });
    this.reservationSuccessModal.open();
    return true;
  }

  requestHeaderAccount() {
    return new Promise((resolve, reject) => {
      const navibar = window.XOYO_top_navi;
      const jquery = window.jQuery;
      if (!navibar || !navibar.xurl || !jquery) {
        reject(new Error('通用头尚未就绪'));
        return;
      }
      jquery.ajax({
        url: `${navibar.xurl}get_info`,
        type: 'GET',
        dataType: 'jsonp',
        timeout: 10000,
        success: response => {
          const account = Number(response.code) > 0 && response.data && response.data.account;
          resolve(typeof account === 'string' ? account : '');
        },
        error: reject
      });
    });
  }

  @action.bound
  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;

    if (this.sideNavigationScrollTarget) {
      this.activeSideNavigationTarget = this.sideNavigationScrollTarget;
      clearTimeout(this.sideNavigationScrollTimer);
      this.sideNavigationScrollTimer = setTimeout(() => {
        // Unlock without overwriting the completed click selection. The next real scroll
        // event resumes position tracking; an idle timer is not a navigation event.
        this.sideNavigationScrollTarget = '';
      }, 120);
      return;
    }

    // Native scrolling rounds coordinates; accept up to 2px of alignment error
    // so a section aligned at a fractional positive top stays active.
    const activationLine = scrollTop + 2;
    const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const lastSectionActivationTop = Math.max(0, maxScrollTop - window.innerHeight * 0.1);
    let activeTarget = SIDE_NAVIGATION_TARGETS[0];
    SIDE_NAVIGATION_TARGETS.forEach(target => {
      const section = document.getElementById(target);
      // Sections are shifted by transform: translateY(-46px), which offsetTop ignores;
      // use getBoundingClientRect so the visual (post-transform) position decides activation.
      const sectionTop = section ? section.getBoundingClientRect().top + scrollTop : Number.POSITIVE_INFINITY;
      if (section && Math.min(sectionTop, lastSectionActivationTop) <= activationLine) {
        activeTarget = target;
      }
    });
    this.activeSideNavigationTarget = activeTarget;
  }

  @action.bound
  collapseSideNavigation() {
    this.isSideNavigationCollapsed = true;
  }

  @action.bound
  expandSideNavigation() {
    this.isSideNavigationCollapsed = false;
  }

  @action.bound
  scrollToSection(target) {
    const section = document.getElementById(target);
    if (!section) {
      return;
    }
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  @action.bound
  onSideNavigationClick(target, title) {
    $$tracker.clickNav(title);
    if (SIDE_NAVIGATION_TARGETS.includes(target)) {
      // Keep the clicked item active throughout native smooth scrolling; otherwise the first
      // intermediate scroll event would immediately restore the section being scrolled from.
      this.sideNavigationScrollTarget = target;
      this.activeSideNavigationTarget = target;
      clearTimeout(this.sideNavigationScrollTimer);
      this.sideNavigationScrollTimer = setTimeout(() => {
        // Unlock without overwriting the completed click selection. The next real scroll
        // event resumes position tracking; an idle timer is not a navigation event.
        this.sideNavigationScrollTarget = '';
      }, 120);
    }
    this.scrollToSection(target);
  }

  backToTop() {
    $$tracker.clickNav('返回顶部');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onCloudGameClick() {
    $$tracker.clickNav('送云游时长');
  }

  onFeatureTopicClick() {
    $$tracker.clickGoSpecial();
    if (!this.featureTopicUrl) {
      message.info('敬请期待', 0.6);
      return;
    }
    const link = document.createElement('a');
    link.href = this.featureTopicUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onOfficialSiteClick() {
    $$tracker.clickGoGuanwang();
  }

  onDownloadClick() {
    $$tracker.clickGoDownload();
  }

  @action.bound
  setServerName(serverName) {
    this.serverName = serverName || '新服名称';
  }

  normalizeConfig(data) {
    const config = data || {};
    const team = config.team || {};
    const bindGold = Math.max(0, Number(config.bind_gold) || 0);
    const purchasePhase = Number(config.buy_status) === 1 ? 'presale' : Number(config.v_exp_time_status) === 1 ? 'supplemental' : 'closed';
    return {
      account: config.account || '',
      buyStatus: Number(config.buy_status),
      bindGold,
      bindGoldText: bindGold >= 10000 ? `${bindGold / 10000}万` : String(bindGold),
      serverName: config.server_name || '新服名称',
      reservationState: Number(config.reserve_status) === 1 ? 'reserved' : 'unreserved',
      isReserved: Number(config.reserve_status) === 1,
      purchasePhase,
      isPurchaseOpen: purchasePhase !== 'closed',
      teamState: team.button_status || 'team_unavailable',
      teamId: team.team_id || ''
    };
  }

  @action.bound
  setConfigViewModel(viewModel) {
    this.configViewModel = viewModel;
    this.setServerName(viewModel.serverName);
  }

  @action.bound
  setConfigReady() {
    this.isConfigReady = true;
  }

  async loadConfig() {
    try {
      const data = await this.$ztService.getConfig();
      const viewModel = this.normalizeConfig(data);
      this.setConfigViewModel(viewModel);
      this.setReservationCount(data.total_activate);
    } catch (error) {
      this.setReservationCount(0);
      return this.configViewModel;
    } finally {
      this.setConfigReady();
    }
    return this.configViewModel;
  }

  normalizeGiftList(list) {
    const giftsById = new Map();
    if (Array.isArray(list)) {
      list.forEach(item => {
        const giftId = Number(item && item.gift_id);
        if (giftId >= 1 && giftId <= GIFT_SLOT_COUNT && !giftsById.has(giftId)) {
          giftsById.set(giftId, item);
        }
      });
    }
    const pricePhase = this.configViewModel.purchasePhase;
    return Array.from({ length: GIFT_SLOT_COUNT }, (item, index) => {
      const giftId = index + 1;
      const gift = giftsById.get(giftId);
      if (!gift) {
        return createEmptyGiftSlot(giftId);
      }
      const isPurchased = Number(gift.status) === 1;
      const canPurchase = !isPurchased && pricePhase !== 'closed';
      return {
        giftId,
        name: gift.name || '',
        purchaseCount: Math.max(0, Number(gift.amount) || 0),
        price: Number(pricePhase === 'supplemental' ? gift.v_exp_price : gift.price) || 0,
        originalPrice: Number(gift.v_exp_price) || 0,
        pricePhase,
        actionState: isPurchased ? 'purchased' : canPurchase ? 'available' : 'unavailable',
        actionLabel: isPurchased ? '已购买' : `购买${gift.name || '礼包'}`,
        canPurchase,
        isActionDisabled: isPurchased,
        isPlaceholder: false
      };
    });
  }

  normalizeTeam(team) {
    const source = team || {};
    const actionState = TEAM_ACTION_LABELS[source.button_status] ? source.button_status : 'team_unavailable';
    return {
      actionState,
      actionLabel: TEAM_ACTION_LABELS[actionState],
      teamId: source.team_id || '',
      memberCount: Math.max(0, Number(source.member_count) || 0),
      isReserved: Number(source.reserve_status) === 1,
      canInteract: ['create_team', 'join_team', 'invite_friend'].includes(actionState)
    };
  }

  get mainTeamViewModel() {
    if (!this.loginStore.isLogin) {
      return {
        actionState: 'create_team',
        actionLabel: TEAM_ACTION_LABELS.create_team,
        teamId: '',
        isReady: true,
        canInteract: true
      };
    }
    const gift = this.giftSlots[0];
    const team = this.accountTeamViewModel;
    const isReady = Boolean(gift && !gift.isPlaceholder && (gift.canPurchase || team));
    let actionState = 'create_team';
    if (isReady && gift.actionState === 'purchased') {
      // Only the account's own team may provide the homepage invitation link.
      const isPendingTeam =
        team.memberCount < 2 &&
        (team.actionState === 'invite_friend' || (team.memberCount === 1 && !['team_full', 'team_success', 'team_unavailable'].includes(team.actionState)));
      actionState = isPendingTeam ? 'invite_friend' : 'team_success';
    }
    return {
      actionState,
      actionLabel: isReady ? TEAM_ACTION_LABELS[actionState] : '组队信息加载中',
      teamId: (team && team.teamId) || '',
      isReady,
      canInteract: isReady && actionState !== 'team_success'
    };
  }

  get teamGuestViewModel() {
    const team = this.teamViewModel;
    const states = {
      invite_friend: 'invite',
      team_success: 'success',
      team_full: 'unavailable',
      team_unavailable: 'pending'
    };
    const actions = {
      create_team: 'join',
      join_team: 'join',
      team_unavailable: 'join',
      invite_friend: 'invite'
    };
    return {
      state: states[team.actionState] || 'pending',
      serverName: this.serverName,
      memberCount: team.memberCount,
      primaryAction: actions[team.actionState] || '',
      primaryLabel: ['create_team', 'team_unavailable'].includes(team.actionState) ? TEAM_ACTION_LABELS.join_team : team.actionLabel,
      canPurchase: ['create_team', 'join_team'].includes(team.actionState),
      canPrimaryAction: ['create_team', 'join_team', 'invite_friend', 'team_unavailable'].includes(team.actionState),
      showPrimary: team.actionState !== 'invite_friend',
      showCreate: !['invite_friend', 'team_success'].includes(team.actionState),
      isReserved: team.isReserved || this.configViewModel.isReserved,
      reserveLabel: team.isReserved || this.configViewModel.isReserved ? '查看活动' : '立即预约新服'
    };
  }

  @action.bound
  setTeamViewModel(team) {
    this.teamViewModel = this.normalizeTeam(team);
  }

  @action.bound
  setGuestActivityEnded(isEnded) {
    this.isGuestActivityEnded = Boolean(isEnded);
  }

  @action.bound
  setHomeActivityEnded(isEnded) {
    this.isHomeActivityEnded = Boolean(isEnded);
  }

  async loadTeamContext(teamId = '', enterGuestMode = true) {
    const contextTeamId = teamId || '';
    this.teamId = contextTeamId;
    if (enterGuestMode) {
      this.isTeamGuestMode = Boolean(contextTeamId);
    }
    try {
      const team = await this.$ztService.teamConfig(contextTeamId);
      this.setGuestActivityEnded(false);
      const hasPurchasedTeamGift = this.giftSlots[0] && this.giftSlots[0].actionState === 'purchased';
      const shouldShowPurchased = hasPurchasedTeamGift && team.button_status !== 'invite_friend';
      this.setTeamViewModel(shouldShowPurchased ? { ...team, button_status: 'team_success' } : team);
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      this.setGuestActivityEnded(Number(response.code) === -10154);
      this.setTeamViewModel({ button_status: Number(response.code) === -10154 ? 'join_team' : 'team_unavailable', team_id: contextTeamId });
    }
    return this.teamViewModel;
  }

  @action.bound
  setGiftSlots(giftSlots) {
    this.giftSlots = giftSlots;
  }

  async loadGiftList() {
    try {
      const data = await this.$ztService.giftList();
      this.setGiftSlots(this.normalizeGiftList(data && data.gift_list));
      // Keep account context separate from team_config's invitation context.
      this.accountTeamViewModel = data && data.team ? this.normalizeTeam(data.team) : null;
      this.setTeamViewModel(data && data.team);
    } catch (error) {
      this.accountTeamViewModel = null;
      this.setGiftSlots(Array.from({ length: GIFT_SLOT_COUNT }, (item, index) => createEmptyGiftSlot(index + 1)));
      return this.giftSlots;
    }
    return this.giftSlots;
  }

  async loadReservationRanking() {
    try {
      const response = await this.$ztService.showAccountList();
      this.setHomeActivityEnded(false);
      this.setReservationHeroes(response && response.data);
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      this.setHomeActivityEnded(Number(response.code) === -10154);
      this.setReservationHeroes([]);
    }
    return this.reservationHeroes;
  }

  getInvitationTeamId() {
    const routerTeamId = this.$searchParams && this.$searchParams.team_id;
    if (routerTeamId) {
      return routerTeamId;
    }
    const locationSearch = typeof window !== 'undefined' ? window.location.search.replace(/^\?/, '') : '';
    const searchTeamId = locationSearch && qs.parse(locationSearch).team_id;
    if (searchTeamId) {
      return searchTeamId;
    }
    const locationHash = typeof window !== 'undefined' ? window.location.hash : '';
    const hashQueryIndex = locationHash.indexOf('?');
    return hashQueryIndex >= 0 ? qs.parse(locationHash.slice(hashQueryIndex + 1)).team_id || '' : '';
  }

  async initializeBusinessState() {
    const queryTeamId = this.getInvitationTeamId();
    const configRequest = this.loadConfig();
    const rankingRequest = this.loadReservationRanking();
    await configRequest;
    await this.loadGiftList();
    if (queryTeamId) {
      // gift_list also carries a team snapshot; the invitation-specific response must win.
      await this.loadTeamContext(queryTeamId);
    }
    await rankingRequest;
    return [this.configViewModel, this.giftSlots, this.teamViewModel, this.reservationHeroes];
  }

  async refreshBusinessState() {
    return this.initializeBusinessState();
  }

  @action.bound
  setReservationCount(reservationCount) {
    const count = Number(reservationCount);
    if (Number.isFinite(count) && count >= 0) {
      this.reservationCount = Math.floor(count);
    }
  }

  normalizeReservationHeroes(list) {
    if (!Array.isArray(list)) {
      return [];
    }
    return list
      .map((item, index) => {
        const serverName = item && typeof item.server === 'string' ? item.server.trim() : '';
        const roleName = item && typeof item.role === 'string' ? item.role.trim() : '';
        if (!serverName && !roleName) {
          return null;
        }
        return {
          id: `${serverName}-${roleName}-${index}`,
          displayText: `【${serverName || '新服名称'}】${roleName || '大侠'}`
        };
      })
      .filter(Boolean)
      .slice(0, 20);
  }

  @action.bound
  setReservationHeroes(list) {
    this.reservationHeroes = this.normalizeReservationHeroes(list);
  }

  get reservationHeroesLoopItems() {
    if (!this.shouldScrollReservationHeroes) {
      return this.reservationHeroes;
    }
    return this.reservationHeroes.concat(
      this.reservationHeroes.map(item => ({
        ...item,
        id: `${item.id}-loop`
      }))
    );
  }

  get shouldScrollReservationHeroes() {
    return this.reservationHeroes.length > RESERVATION_HERO_VISIBLE_COUNT;
  }

  async fetchConfig() {
    return this.loadConfig();
  }

  async fetchReservationHeroes() {
    return this.loadReservationRanking();
  }

  async handleGiftClick(giftId) {
    const gift = this.giftSlots.find(item => item.giftId === Number(giftId));
    $$tracker.clickBuyGoods((gift && gift.name) || '');
    if (!this.loginStore.isLogin) {
      this.signIn();
      return false;
    }
    if (!gift || !gift.canPurchase) {
      this.showBusinessMessage(gift && gift.actionState === 'purchased' ? '该礼包已购买' : '活动已结束');
      return false;
    }
    return this.createGiftOrder(gift.giftId, '', true);
  }

  async handleTeamGiftClick() {
    const { actionState, teamId, isReady } = this.mainTeamViewModel;
    if (actionState === 'create_team') {
      $$tracker.clickCreateTeam();
    }
    if (actionState === 'team_success') {
      return false;
    }
    if (this.isHomeActivityEnded) {
      this.showBusinessMessage('活动已结束');
      return false;
    }
    if (!this.loginStore.isLogin) {
      this.signIn();
      return false;
    }
    if (!isReady) {
      return false;
    }
    if (actionState === 'create_team') {
      return this.createGiftOrder(1, '', true);
    }
    if (actionState === 'invite_friend') {
      return this.copyTeamInvite(teamId);
    }
    return false;
  }

  handleGuestJoin() {
    $$tracker.clickJoinTeam(this.teamId || '');
    if (this.configViewModel.purchasePhase === 'closed' || this.isGuestActivityEnded) {
      this.showBusinessMessage('活动已结束');
      return false;
    }
    if (this.teamViewModel.actionState === 'team_unavailable') {
      this.showBusinessMessage('队伍已失效');
      return false;
    }
    if (!this.loginStore.isLogin) {
      this.signIn();
      return false;
    }
    if (!['create_team', 'join_team'].includes(this.teamViewModel.actionState)) {
      return false;
    }
    return this.createGiftOrder(1, this.teamId, true, true);
  }

  handleGuestCreate() {
    $$tracker.clickCreateTeamLink(this.teamId || '');
    return this.navigateToMainReservation();
  }

  handleGuestReserveAction() {
    $$tracker.clickSubscribeLink(this.teamId || '');
    return this.navigateToMainReservation();
  }

  // 客态仅加入队伍可拉起支付；创建和预约入口只切回主预约页，各自保留点击埋点。
  navigateToMainReservation() {
    this.returnToGuestAfterReservation = Boolean(this.teamId) && !this.teamGuestViewModel.isReserved;
    this.isTeamGuestMode = false;
    setTimeout(() => this.scrollToSection('reservation'), 0);
    return true;
  }

  @action.bound
  handleReservationSuccessClose() {
    if (!this.returnToGuestAfterReservation || !this.teamId) {
      return false;
    }
    this.returnToGuestAfterReservation = false;
    this.isTeamGuestMode = true;
    return true;
  }

  async createGiftOrder(giftId, teamId, hasVerifiedLogin = false, shouldRedirect = false) {
    if (this.isOrdering) {
      return false;
    }
    this.isOrdering = true;
    try {
      if (!hasVerifiedLogin && !(await this.getIsLogin())) {
        this.signIn();
        return false;
      }
      const result = await this.$ztService.createOrder(giftId, 'pc', window.location.href, teamId || '');
      if (!result || !result.pay_url) {
        this.showBusinessMessage('支付地址获取失败，请稍后重试');
        return false;
      }
      if (Number(giftId) === 1 && result.team_id) {
        this.teamId = result.team_id;
      }
      if (shouldRedirect) {
        window.location.href = result.pay_url;
        return true;
      }
      if (typeof this.paymentModalStore.setPayUrl === 'function') {
        this.paymentModalStore.setPayUrl(result.pay_url);
      } else {
        this.paymentModalStore.payUrl = result.pay_url;
      }
      this.paymentModal.open();
      return true;
    } catch (error) {
      const response = (error && error.serviceResponse) || error || {};
      if (Number(giftId) === 1 && Number(response.code) === -20109) {
        this.setGiftSlots(
          this.giftSlots.map(gift => (gift.giftId === 1 ? { ...gift, actionState: 'purchased', canPurchase: false, isActionDisabled: true } : gift))
        );
        this.accountTeamViewModel = this.normalizeTeam({ button_status: 'team_success' });
        this.setTeamViewModel({
          button_status: 'team_success',
          member_count: this.teamViewModel.memberCount,
          reserve_status: this.teamViewModel.isReserved ? 1 : 0,
          team_id: this.teamViewModel.teamId || this.teamId
        });
        return false;
      }
      this.showBusinessMessage(response.message || response.msg || '创建订单失败，请稍后重试');
      return false;
    } finally {
      this.isOrdering = false;
    }
  }

  buildTeamInviteUrl(teamId = '') {
    if (!teamId || typeof window === 'undefined') {
      return '';
    }
    // The backend owns team data only; the current deployment URL keeps test and production links environment-safe.
    const link = document.createElement('a');
    link.href = window.location.href;
    link.search = `?team_id=${encodeURIComponent(teamId)}`;
    link.hash = '/';
    return link.href;
  }

  async copyTeamInvite(teamId = this.teamViewModel.teamId || this.teamId) {
    $$tracker.clickUrlBuy();
    const inviteUrl = this.buildTeamInviteUrl(teamId);
    if (!inviteUrl) {
      this.showBusinessMessage('邀请链接暂不可用');
      return false;
    }
    try {
      const response = await Services.createTinyUrl(inviteUrl);
      const tinyUrl = response && response.tiny_url;
      if (!tinyUrl) {
        throw new Error('Missing tiny_url');
      }
      const shareText = `我已预约《剑侠世界》首个生态服，快和我组队购买钜惠礼包，链接：${tinyUrl}，一起玩新服这波福利不能错过！`;
      const isCopied = copy(shareText);
      this.showBusinessMessage(isCopied ? '邀请链接已复制' : '复制失败，请手动复制');
      return isCopied;
    } catch (error) {
      this.showBusinessMessage('邀请链接生成失败，请稍后重试');
      return false;
    }
  }

  async handlePaymentClose() {
    await this.loadConfig();
    await this.loadGiftList();
    if (this.isTeamGuestMode && this.teamId) {
      await this.loadTeamContext(this.teamId, false);
    }
  }
}

export default MainStore;
